import { ref, computed, reactive, unref, nextTick } from 'vue'
import { defineStore } from 'pinia'
import {
  type Transact,
  type Payslip,
  type Period,
  ACCOUNT_TYPE,
  ACCOUNT_SIDE,
  TRANSACT_SOURCE,
  TRANSACT_EXAMPLE_NAME,
  type Account,
  type Employee
} from './types'
import { useUtil } from '@/composables/util'
import { faker } from '@faker-js/faker'
// import { useEmployeeStore } from './employee'
import { usePayslipStore } from './payslip'
import { useAccountStore } from './account'
import { useEmployeeStore } from './employee'
import { useAppStore } from './app'
import dayjs from 'dayjs'
import axios from 'axios'
import { API } from '@/config'

const apiURL: string = API
// const apiURL = "http://localhost:4000/api"

const api = axios.create({
  baseURL: apiURL + '/v1'
})

// The guide row UploadFile exports for an empty account. Matched on its sentinel name,
// or on the id 0 it ships with, since a clerk may clear the name but leave the id.
// Number('') and Number('0') are both 0; a typo'd id like 'AB12' is NaN and falls through
// to the normal "employee id AB12 not found" message.
export const isExampleRow = (transact: any) =>
  String(transact?.employee_name ?? '').trim().toUpperCase() ==
    TRANSACT_EXAMPLE_NAME.toUpperCase() || Number(transact?.id) === 0

const my = useUtil()

export const useTransactStore = defineStore('transact', () => {
  // Transact Detail
  const transactions = reactive(Array<Transact>())
  const payslipTransactions = reactive(Array<Transact>())

  const selectedTransaction = ref({} as Transact) // selected in table
  const selectedEmployee = ref({} as Employee)
  const selectedFullname = ref('')
  const thisTransaction = ref({} as Transact) //for form editing

  const thisDate = ref(null)
  const thisAccount = ref({} as Account)

  const showForm = ref(false)

  // Amount most recently sent for each system transaction. The store row only
  // updates when the PUT answers, so calcPay reads this to mirror a withheld
  // earning without waiting on the network.
  const writtenAmounts = new Map<string, number>()

  const payslipStore = usePayslipStore()
  const accountStore = useAccountStore()
  const employeeStore = useEmployeeStore()
  const appStore = useAppStore()

  async function generateTransactions(payload: Payslip) {
    const payslip_uuid = payload.uuid
    const period_uuid = payload.period_uuid
    const employee_uuid = payload.employee_uuid

    for (const account of accountStore.accounts) {
      const transact = {} as Transact

      const uuid = faker.string.uuid()
      transact.uuid = uuid

      transact.account_uuid = account.uuid
      transact.payslip_uuid = payload.uuid
      transact.period_uuid = payload.period_uuid
      transact.employee_uuid = payload.employee_uuid
      transact.amount = 0.0
      transact.account_side = account.account_side
      transact.source = TRANSACT_SOURCE.System

      transact.transact_date = dayjs().toDate()
      transact.account = account
      await addTransaction(transact)
    }
  }

  // Strip nested objects that web2py rejects as invalid foreign-key references
  function toApiPayload(payload: Transact) {
    // employee_id is a client-side convenience field — the transactions table has
    // no such column and the model does not define it. web2py rejects the entire
    // insert on an unknown field, so it must never reach the API.
    //
    // The audit columns go too. An edited row is a copy of what the API returned, so
    // it carries created_on/modified_on/ip; sending them back makes the API accept
    // the stale values and modified_on stops advancing, hiding when a row last
    // changed. Left out, web2py stamps them itself.
    const {
      account,
      payslip,
      employee_id,
      created_on,
      modified_on,
      created_by,
      modified_by,
      ip,
      ...rest
    } = payload as any
    return rest
  }

  async function addTransaction(payload: Transact) {
    const response = await api.post('/transactions.json', toApiPayload(payload))

    if (response.status == 200) {
      transactions.push(response.data)
    } else {
    }
  }

  // In-flight updateTransaction calls. calcPay fires them without awaiting, then
  // totals the payslip from payslipTransactions; settled() lets it wait until every
  // PUT response has been spliced in, so the totals never sum pre-write amounts.
  const pendingUpdates = new Set<Promise<unknown>>()
  const settled = () => Promise.allSettled([...pendingUpdates])

  function updateTransaction(payload: Transact) {
    const run = writeTransaction(payload)
    pendingUpdates.add(run)
    return run.finally(() => pendingUpdates.delete(run))
  }

  async function writeTransaction(payload: Transact) {
    // Never overwrite user-provided (imported/manual) transactions. calcPay's
    // recompute blocks spread the existing row into the payload, so a protected
    // row's source travels here and we bail before the PUT.
    if (payload.source && payload.source !== TRANSACT_SOURCE.System) return

    if (payload.uuid) {
      writtenAmounts.set(payload.uuid, payload.amount * 1 || 0)
      await api.put('/transactions/' + payload.uuid, toApiPayload(payload)).then((response: any) => {
        if (response.status == 200) {
          const idx = payslipTransactions.filter((item: Transact) => item != undefined).findIndex((item: Transact) => item.uuid == payload.uuid)
          if (idx >= 0) payslipTransactions.splice(idx, 1, response.data)
        } else {
        }
      })

    }  else {

    }
    
  }

  // Returns whether the row actually persisted. A failed insert still answers 200 —
  // the body carries {errors:{...}} instead of the saved row — so the status alone
  // would report success and push a bodyless object into the list, which then shows
  // nothing because it has no account_uuid to filter on.
  async function saveTransaction(payload: Transact) {
    if (payload.uuid==undefined || !payload.uuid) {
      const response = await api.post('/transactions.json', toApiPayload(payload))

      if (response.status != 200 || !response.data?.uuid) return false

      transactions.push(response.data)
      return true

    } else {
      const response = await api.put('/transactions/' + payload.uuid, toApiPayload(payload))

      // On a matched update the API echoes the stored row; when nothing matched it
      // echoes the payload back instead, which has no created_on to give it away.
      if (response.status != 200 || !response.data?.created_on) return false

      replaceTransaction(response.data)
      return true
    }
  }
  async function getPeriodTransactions(payload: Period) {

    const response = await api.get('/transactions/period-uuid/' + payload.uuid)
    if (response.status == 200) {
      // transactions.length = 0
      // response.data.data.forEach((item: Transact) => {
      //   transactions.push(item)
      // })
      Object.assign(transactions,[])
      Object.assign(transactions,response.data.data)
    } else {
    }
  }

  // What each employee had in the selected account before the selected period (server:
  // modules/account_xtd.py). The period's own rows are added live in accountToDate, so an
  // edit or import shows without another round trip.
  type Prior = { mtd: number; ytd: number; total: number }
  const accountPrior = ref({} as Record<string, Prior>)
  let priorKey = ''

  async function getAccountPrior(account_uuid?: string, period_uuid?: string) {
    const key = `${account_uuid}|${period_uuid}`
    priorKey = key
    if (!account_uuid || !period_uuid) {
      accountPrior.value = {}
      return
    }
    try {
      const response = await api.get('/account_xtd/' + account_uuid + '?period=' + period_uuid)
      // a newer account/period was picked while this one was in flight
      if (priorKey !== key) return
      const next = {} as Record<string, Prior>
      for (const row of response.data?.data ?? []) {
        next[row.employee_uuid] = { mtd: Number(row.mtd) || 0, ytd: Number(row.ytd) || 0, total: Number(row.total) || 0 }
      }
      accountPrior.value = next
    } catch {
      if (priorKey === key) accountPrior.value = {}
    }
  }

  async function getPayslipTransactions(payload: Payslip) {

    const response = await api.get('/transactions/payslip-uuid/' + payload.uuid)
    if (response.status == 200) {
      Object.assign(payslipTransactions,[])
      Object.assign(payslipTransactions,response.data.data)
    } else {
    }
  }

  // @ts-ignore
  const selectedPayslipEarnings = computed(() => {
    return payslipTransactions.filter((item: Transact) =>item !=undefined).filter(
      // @ts-ignore
      (item: Transact) =>
        item.payslip_uuid == payslipStore.selectedPayslip.uuid && item.account_side == ACCOUNT_SIDE.Debit
    )
        // @ts-ignore
        .sort((a,b)=>{
          const a_number = Number.parseInt((accountStore.accounts.find((item: Account) => item.uuid == a.account_uuid)?.account_number || '0' ))
          const b_number = Number.parseInt(accountStore.accounts.find((item: Account) => item.uuid == b.account_uuid)?.account_number || '0')

          if (a_number > b_number) {
            return 1
          } else if (a_number < b_number) {
            return -1
          } else {
            0
          }

  })

    })

  // @ts-ignore
  const selectedPayslipDeductions = computed(() => {
    return payslipTransactions.filter((item: Transact) =>item !=undefined).filter(
      // @ts-ignore
      (item: Transact) =>
        item.payslip_uuid == payslipStore.selectedPayslip.uuid && item.account_side == ACCOUNT_SIDE.Credit
    )
    // @ts-ignore
    .sort((a,b)=>{
      const a_number = Number.parseInt((accountStore.accounts.find((item: Account) => item.uuid == a.account_uuid)?.account_number || '0' ))
      const b_number = Number.parseInt(accountStore.accounts.find((item: Account) => item.uuid == b.account_uuid)?.account_number || '0')

      if (a_number > b_number) {
        return 1
      } else if (a_number < b_number) {
        return -1
      } else {
        0
      }

})

  })

  // @ts-ignore
  const totalEarnings = computed(() => (payslip_uuid: string)=> {
   
      // @ts-ignore
      const amount = payslipTransactions.filter(item=>item!=undefined).filter(
        (item: Transact) => item.payslip_uuid == payslip_uuid && item.account_side == ACCOUNT_SIDE.Debit
      ).map((item: Transact) => item.amount).reduce((tot: number,curr: number,i: number)=>tot+curr,0)

      payslipStore.thisPayslip.total_debit = amount // intentional side effect
      return amount
  })

  // @ts-ignore
  const totalDeductions = computed(() => (payslip_uuid: string) => {

      // @ts-ignore
      const amount =  payslipTransactions.filter(item=>item!=undefined).filter(
        (item: Transact) => item.payslip_uuid ==  payslip_uuid && item.account_side == ACCOUNT_SIDE.Credit
      ).map((item: Transact) => item.amount).reduce((tot: number,curr: number,i: number)=>tot+curr,0)
  
      
      payslipStore.thisPayslip.total_credit = amount // intentional side effect
      return amount
  })

  const netPay = computed(() => (payslip_uuid: string) => {
    // @ts-ignore
    const amount =  payslipTransactions.filter(item=>item!=undefined).filter(
      (item: Transact) => item.payslip_uuid == payslip_uuid
    ).map((item: Transact) => item.account_side==ACCOUNT_SIDE.Debit ? item.amount : -item.amount).reduce((tot: number,curr: number,i: number)=>tot+curr,0)
    
    payslipStore.thisPayslip.net_pay = amount // intentional side effect

    return amount
  })

 
  // by transact_date and account
  const transactionsByAccount = computed(() => {
    const tx =  transactions.filter((item: Transact) => item.account_uuid == appStore.thisAccount.uuid)
    return tx
  })

  // This Period, MTD, YTD and Total (as of the selected period) per employee for the
  // selected account: the server's prior amounts plus this period's rows.
  const accountToDate = computed(() => {
    const out = {} as Record<string, { period: number; mtd: number; ytd: number; total: number }>
    for (const item of transactionsByAccount.value) {
      const mine = (out[item.employee_uuid] ??= { period: 0, mtd: 0, ytd: 0, total: 0 })
      mine.period += Number(item.amount) || 0
    }
    for (const [uuid, mine] of Object.entries(out)) {
      const before = accountPrior.value[uuid]
      mine.mtd = (before?.mtd ?? 0) + mine.period
      mine.ytd = (before?.ytd ?? 0) + mine.period
      mine.total = (before?.total ?? 0) + mine.period
    }
    return out
  })

  const totalByAccount = computed(() => {
    // `curr + 1` here used to add 1 per row, so the footer total was inflated by
    // the row count (15 rows read 15.00 too high).
    const total = transactions
      .filter((item: Transact) => item.account_uuid == appStore.thisAccount.uuid)
      .map((item: Transact) => Number(item.amount) || 0)
      .reduce((tot: number, curr: number) => tot + curr, 0)
    return total
  })

  // Only user-provided rows may be removed or cleared; system rows belong to calcPay.
  const isEditable = (item: Transact) =>
    item != undefined && !accountStore.isCalculatedAccount(item.account_uuid)

  const isUserProvided = (item: Transact) =>
    item != undefined && !!item.source && item.source !== TRANSACT_SOURCE.System

  // Rows in the current account that Clear All / Delete are allowed to touch.
  const clearableByAccount = computed(() =>
    transactions.filter(
      (item: Transact) => item.account_uuid == appStore.thisAccount.uuid && isUserProvided(item)
    )
  )

  function forgetTransaction(uuid: string) {
    const drop = (list: Transact[]) => {
      const idx = list.findIndex((item: Transact) => item != undefined && item.uuid == uuid)
      if (idx >= 0) list.splice(idx, 1)
    }
    drop(transactions)
    drop(payslipTransactions)
  }

  function replaceTransaction(row: Transact) {
    const swap = (list: Transact[]) => {
      const idx = list.findIndex((item: Transact) => item != undefined && item.uuid == row.uuid)
      if (idx >= 0) list.splice(idx, 1, row)
    }
    swap(transactions)
    swap(payslipTransactions)
  }

  // Hard delete. Refuses system rows: generateTransactions seeds one row per
  // account per payslip and calcPay writes into that row, so removing it would
  // leave the account with no slot to recompute into.
  async function deleteTransaction(payload: Transact) {
    if (!payload?.uuid || !isUserProvided(payload)) return false

    // The API answers 200 with {deleted: 0} when nothing matched, so the status
    // alone would report a phantom success. Trust the row count.
    const response = await api.delete('/transactions/' + payload.uuid)
    if (response.status != 200 || !Number(response.data?.deleted)) return false

    forgetTransaction(payload.uuid)
    if (payload.payslip_uuid) await payslipStore.dirtyPayslip(payload.payslip_uuid)
    if (selectedTransaction.value?.uuid == payload.uuid) selectedTransaction.value = {} as Transact
    return true
  }

  // A deliberate user edit of an existing row, as opposed to calcPay's recompute.
  // It goes through saveTransaction, which has no provenance guard — the guard in
  // updateTransaction exists to stop *recalc* from clobbering user figures, and must
  // not stop the user from changing them by hand.
  //
  // Editing a row by hand makes it user-provided, so the next recalc leaves it alone:
  // an imported row keeps 'import' (that is where it came from), while a system row
  // becomes 'manual'. Both are protected identically by isUserProvided.
  async function editTransaction(payload: Transact) {
    if (!payload?.uuid) return false

    // Calculated accounts (basic salary, overtime, contributions, tax, 13th month,
    // withheld mirrors...) belong to calcPay; only the free-form ones are editable.
    if (accountStore.isCalculatedAccount(payload.account_uuid)) return false

    // Reject a missing amount before Number() launders it: Number('') and
    // Number(null) are both 0, so a cleared field would silently save as 0.00 and
    // wipe the figure the user meant to change. Non-numeric text is NaN, which the
    // API would reject with no useful message.
    const raw = payload.amount
    if (raw === null || raw === undefined || String(raw).trim() == '') return false
    const amount = Number(raw)
    if (!Number.isFinite(amount)) return false

    // The datepicker hands back a Date. JSON.stringify would send UTC ISO with a
    // Z, shifting PH times back 8 hours and sometimes filing the row under the
    // previous day, so format it the way the API stores it. An unparseable value
    // leaves the field as it was rather than blanking a required column.
    const transact_date = my.toApiDateTime(payload.transact_date) ?? payload.transact_date

    const next = {
      ...payload,
      amount,
      transact_date,
      source: isUserProvided(payload) ? payload.source : TRANSACT_SOURCE.Manual
    } as Transact

    if (!(await saveTransaction(next))) return false

    // the payslip's totals no longer match its rows
    if (next.payslip_uuid) await payslipStore.dirtyPayslip(next.payslip_uuid)
    return true
  }

  // Undo an import without destroying the row: zero the amount and hand the row
  // back to calcPay by restoring source='system'. The next recalc refills it.
  async function clearTransactionsByAccount() {
    const targets = clearableByAccount.value.slice()
    let cleared = 0
    const dirtied = new Set<string>()

    for (const target of targets) {
      if (!target.uuid) continue
      const payload = { ...target, amount: 0, source: TRANSACT_SOURCE.System }
      const response = await api.put('/transactions/' + target.uuid, toApiPayload(payload))
      if (response.status != 200) continue

      replaceTransaction(response.data ?? payload)
      cleared++
      if (target.payslip_uuid) dirtied.add(target.payslip_uuid)
    }

    for (const payslip_uuid of dirtied) await payslipStore.dirtyPayslip(payslip_uuid)

    return { cleared, total: targets.length }
  }

  const importTransactions = async (rows: any) => {
    let imported = 0
    let examples = 0
    const skipped = [] as { row: number; reason: string }[]

    for (let index = 0; index < rows.length; index++) {
      const transact = rows[index]
      const row = index + 1 // 1-based data row for user-facing feedback

      // A wholly blank row is spreadsheet residue, not data. Drop it silently —
      // reporting it as "account  not found" only sends the user hunting for a
      // problem in a row they never typed.
      if (Object.values(transact ?? {}).every((value) => String(value ?? '').trim() == '')) continue

      // Our own guide row, left in place. Scaffolding rather than a failed row, so it is
      // counted apart from skipped and never reported as an error.
      if (isExampleRow(transact)) {
        examples++
        continue
      }

      const payload = {} as Transact

      const account = accountStore.accounts.find(
        (item: Account) => Number(item.account_number) == Number(transact.account_number)
      )
      if (account == undefined) {
        skipped.push({ row, reason: `account ${transact.account_number} not found` })
        continue
      }

      const employee = employeeStore.employees.find((item: Employee) => Number(item.employee_id) == Number(transact.id))
      if (employee == undefined) {
        skipped.push({ row, reason: `employee id ${transact.id} not found` })
        continue
      }

      // attach to payslip
      const payslip = payslipStore.payslips.find(
        (payslip: Payslip) =>
          payslip.period_uuid == appStore.thisPeriod.uuid && payslip.employee_uuid == employee?.uuid
      )

      if (payslip == undefined) {
        skipped.push({ row, reason: `no payslip for employee id ${transact.id} in this period` })
        continue
      }

      payload.account_uuid = account?.uuid
      payload.employee_uuid = employee?.uuid || ''
      payload.employee_id = transact.id
      payload.amount = Number(transact.amount) || 0
      // side is authoritative from the account, not the CSV
      payload.account_side = account.account_side
      payload.account = account
      payload.payslip_uuid = payslip?.uuid
      payload.period_uuid = payslip?.period_uuid
      payload.source = TRANSACT_SOURCE.Import

      // use period start date for now
      payload.transact_date = appStore.thisPeriod.start_date

      // Upsert: an explicit uuid in the CSV wins (export round-trip); otherwise
      // match the existing row for this payslip+account so re-imports update in
      // place instead of duplicating. generateTransactions seeds one row per
      // account per payslip, so this normally reuses that row.
      if (transact.uuid) {
        payload.uuid = transact.uuid
      } else {
        const matches = (item: Transact) =>
          item != undefined &&
          item.payslip_uuid == payslip?.uuid &&
          item.account_uuid == account?.uuid
        // transactions holds all period rows; payslipTransactions the selected slip
        const existing = transactions.find(matches) || payslipTransactions.find(matches)
        if (existing?.uuid) payload.uuid = existing.uuid
      }

      // Count only what actually persisted — "Imported 4" against an empty list is
      // worse than an honest error. Dirty the payslip after the save, not before,
      // so a rejected row does not mark a payslip for recalc it never changed.
      if (!(await saveTransaction(payload))) {
        skipped.push({ row, reason: `server rejected the row for employee id ${transact.id}` })
        continue
      }

      await payslipStore.dirtyPayslip(payslip?.uuid as string)
      imported++
    }

    return { imported, skipped, examples }
  }

  return {
    showForm,
    transactions,
    payslipTransactions,
    writtenAmounts,
    selectedTransaction,
    thisTransaction,
    selectedEmployee,
    selectedFullname,
    // selectedPayslipTransactions,
    generateTransactions,
    // allTransactions,
    getPeriodTransactions,
    getPayslipTransactions,

    selectedPayslipEarnings,
    selectedPayslipDeductions,

    thisDate,
    thisAccount,
    transactionsByAccount,
    accountToDate,
    getAccountPrior,
    importTransactions,
    updateTransaction,
    settled,
    saveTransaction,
    editTransaction,
    deleteTransaction,
    clearTransactionsByAccount,
    clearableByAccount,
    isUserProvided,
    isEditable,

    totalEarnings,
    totalDeductions,
    netPay,

    totalByAccount
    
  }
})
